import ModalPortal from '../../../../components/ModalPortal';

function Overlay({ children }) {
    return (
        <ModalPortal>
            <div className="modal-overlay fade-in">{children}</div>
        </ModalPortal>
    );
}

/**
 * RuleManagerModals - All modal dialogs for RuleManager
 * Contains: Create Ruleset, Rename Ruleset, Rename Family, Delete Family, Consistency Check
 */
export default function RuleManagerModals({
    modalType,
    modalTargetFamily,
    modalTargetRuleSet,
    modalTargetFolder,
    modalInputVal,
    deleteRuleSetsCheck,
    isSaving,
    isCheckingConsistency,
    consistencyResult,
    families,
    activeRuleSetData,
    activeRuleSetId,
    currentContext,
    draftRuleSetData,
    originalRuleSetData,
    blocker,
    handleCreateRuleSetSubmit,
    handleCreateFolderSubmit,
    handleRenameFolderSubmit,
    handleRenameRuleSetSubmit,
    handleRenameFamilySubmit,
    handleDeleteFamilySubmit,
    handleAddCommentSubmit,
    handleSaveChanges,
    setModalType,
    setModalInputVal,
    setDeleteRuleSetsCheck,
    setDraftRuleSetData,
    loadMenu,
    onDataChanged,
    setActiveRuleSetId,
    setActiveRuleSetData
}) {

    // ========================================================================
    // CREATE RULESET MODAL
    // ========================================================================
    if (modalType === 'createRuleset') {
        const canCreate = (modalInputVal || '').trim().length > 0;
        return (
            <Overlay>
                <div className="modal-content" style={{ maxWidth: '400px', padding: '1.25rem' }}>
                    <h2>Create New Rule Table</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        Add a new empty rule table under <strong>{modalTargetFamily?.name === 'Global' ? 'Global scope' : `family: ${modalTargetFamily?.name}`}</strong>
                        {modalTargetFolder?.name ? <> in folder <strong>{modalTargetFolder.name}</strong></> : null}.
                    </p>
                    <form onSubmit={handleCreateRuleSetSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                                Rule Table Name <span style={{ color: 'var(--error)' }}>*</span>
                            </label>
                            <input 
                                type="text" 
                                className="input-field" 
                                placeholder="e.g., Engine_Oil_Compatibility" 
                                value={modalInputVal} 
                                onChange={e => setModalInputVal(e.target.value)} 
                                autoFocus 
                                required 
                                aria-required="true"
                            />
                        </div>
                        <div className="modal-actions" style={{ marginTop: '1rem', alignItems: 'center' }}>
                            {!canCreate && (
                                <span style={{ marginRight: 'auto', fontSize: '0.85rem', color: 'var(--error)', fontWeight: '600' }}>
                                    Enter a name to create the rule table
                                </span>
                            )}
                            <button type="button" className="btn-secondary" onClick={() => setModalType(null)} disabled={isSaving}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={isSaving || !canCreate}>{isSaving ? 'Creating...' : 'Create'}</button>
                        </div>
                    </form>
                </div>
            </Overlay>
        );
    }

    if (modalType === 'createFolder') {
        const canCreate = (modalInputVal || '').trim().length > 0;
        return (
            <Overlay>
                <div className="modal-content" style={{ maxWidth: '400px', padding: '1.25rem' }}>
                    <h2>Create Folder</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        Group rule tables in <strong>{modalTargetFamily?.name}</strong>.
                    </p>
                    <form onSubmit={handleCreateFolderSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                                Folder Name <span style={{ color: 'var(--error)' }}>*</span>
                            </label>
                            <input
                                type="text"
                                className="input-field"
                                placeholder="e.g., Engine"
                                value={modalInputVal}
                                onChange={e => setModalInputVal(e.target.value)}
                                autoFocus
                                required
                                aria-required="true"
                            />
                        </div>
                        <div className="modal-actions" style={{ marginTop: '1rem', alignItems: 'center' }}>
                            {!canCreate && (
                                <span style={{ marginRight: 'auto', fontSize: '0.85rem', color: 'var(--error)', fontWeight: '600' }}>
                                    Enter a name to create the folder
                                </span>
                            )}
                            <button type="button" className="btn-secondary" onClick={() => setModalType(null)} disabled={isSaving}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={isSaving || !canCreate}>{isSaving ? 'Creating...' : 'Create'}</button>
                        </div>
                    </form>
                </div>
            </Overlay>
        );
    }

    if (modalType === 'renameFolder') {
        const canRename = (modalInputVal || '').trim().length > 0;
        return (
            <Overlay>
                <div className="modal-content" style={{ maxWidth: '400px', padding: '1.25rem' }}>
                    <h2>Rename Folder</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        Change the name of <strong>{modalTargetFolder?.name}</strong>.
                    </p>
                    <form onSubmit={handleRenameFolderSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                                Folder Name <span style={{ color: 'var(--error)' }}>*</span>
                            </label>
                            <input
                                type="text"
                                className="input-field"
                                placeholder="e.g., Engine"
                                value={modalInputVal}
                                onChange={e => setModalInputVal(e.target.value)}
                                autoFocus
                                required
                                aria-required="true"
                            />
                        </div>
                        <div className="modal-actions" style={{ marginTop: '1rem', alignItems: 'center' }}>
                            {!canRename && (
                                <span style={{ marginRight: 'auto', fontSize: '0.85rem', color: 'var(--error)', fontWeight: '600' }}>
                                    Enter a name to rename the folder
                                </span>
                            )}
                            <button type="button" className="btn-secondary" onClick={() => setModalType(null)} disabled={isSaving}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={isSaving || !canRename}>{isSaving ? 'Renaming...' : 'Rename'}</button>
                        </div>
                    </form>
                </div>
            </Overlay>
        );
    }

    // ========================================================================
    // RENAME RULESET MODAL
    // ========================================================================
    if (modalType === 'renameRuleset') {
        const canRename = (modalInputVal || '').trim().length > 0;
        return (
            <Overlay>
                <div className="modal-content" style={{ maxWidth: '400px', padding: '1.25rem' }}>
                    <h2>Rename Rule Table</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        Change the name of <strong>{modalTargetRuleSet?.name}</strong>.
                    </p>
                    <form onSubmit={handleRenameRuleSetSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                                Rule Table Name <span style={{ color: 'var(--error)' }}>*</span>
                            </label>
                            <input
                                type="text"
                                className="input-field"
                                placeholder="e.g., Engine_Oil_Compatibility"
                                value={modalInputVal}
                                onChange={e => setModalInputVal(e.target.value)}
                                autoFocus
                                required
                                aria-required="true"
                            />
                        </div>
                        <div className="modal-actions" style={{ marginTop: '1rem', alignItems: 'center' }}>
                            {!canRename && (
                                <span style={{ marginRight: 'auto', fontSize: '0.85rem', color: 'var(--error)', fontWeight: '600' }}>
                                    Enter a name to rename the rule table
                                </span>
                            )}
                            <button type="button" className="btn-secondary" onClick={() => setModalType(null)} disabled={isSaving}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={isSaving || !canRename}>{isSaving ? 'Renaming...' : 'Rename'}</button>
                        </div>
                    </form>
                </div>
            </Overlay>
        );
    }

    // ========================================================================
    // RENAME FAMILY MODAL
    // ========================================================================
    if (modalType === 'renameFamily') {
        return (
            <Overlay>
                <div className="modal-content" style={{ maxWidth: '400px', padding: '1.25rem' }}>
                    <h2>Rename Family</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        Change the name of the family.
                    </p>
                    <form onSubmit={handleRenameFamilySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <input 
                            type="text" 
                            className="input-field" 
                            placeholder="New Family Name" 
                            value={modalInputVal} 
                            onChange={e => setModalInputVal(e.target.value)} 
                            autoFocus 
                            required 
                        />
                        <div className="modal-actions" style={{ marginTop: '1rem' }}>
                            <button type="button" className="btn-secondary" onClick={() => setModalType(null)} disabled={isSaving}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={isSaving || !modalInputVal}>{isSaving ? 'Renaming...' : 'Rename'}</button>
                        </div>
                    </form>
                </div>
            </Overlay>
        );
    }

    // ========================================================================
    // ADD COMMENT MODAL
    // ========================================================================
    if (modalType === 'addComment') {
        return (
            <Overlay>
                <div className="modal-content" style={{ maxWidth: '440px', padding: '1.25rem' }}>
                    <h2>Add comment</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                        Add a note to <strong>{modalTargetRuleSet?.name}</strong>.
                    </p>
                    <form onSubmit={handleAddCommentSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <textarea
                            className="input-field"
                            placeholder="Write a comment..."
                            value={modalInputVal}
                            onChange={e => setModalInputVal(e.target.value)}
                            autoFocus
                            required
                            rows={4}
                            style={{ resize: 'vertical', minHeight: '6rem' }}
                        />
                        <div className="modal-actions" style={{ marginTop: '1rem' }}>
                            <button type="button" className="btn-secondary" onClick={() => setModalType(null)} disabled={isSaving}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={isSaving || !modalInputVal.trim()}>{isSaving ? 'Saving...' : 'Add comment'}</button>
                        </div>
                    </form>
                </div>
            </Overlay>
        );
    }

    // ========================================================================
    // DELETE FAMILY MODAL
    // ========================================================================
    if (modalType === 'deleteFamily') {
        return (
            <Overlay>
                <div className="modal-content" style={{ maxWidth: '400px', padding: '1.25rem' }}>
                    <h2 style={{ color: 'var(--error)' }}>Delete Family</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.4', marginBottom: '1rem' }}>
                        Are you sure you want to delete family <strong>{modalTargetFamily?.name}</strong>?
                    </p>
                    <p style={{ background: 'rgba(220, 38, 38, 0.08)', borderLeft: '3px solid var(--error)', padding: '0.75rem', fontSize: '0.85rem', color: 'var(--error)', lineHeight: '1.4', marginBottom: '1rem' }}>
                        <strong>Warning:</strong> Any local features defined only for this family will be permanently deleted. To save them, please convert them to global features in the feature scoping manager before deleting this family.
                    </p>
                    <form onSubmit={handleDeleteFamilySubmit}>
                        <label className="modal-checkbox-row">
                            <input 
                                type="checkbox" 
                                checked={deleteRuleSetsCheck} 
                                onChange={e => setDeleteRuleSetsCheck(e.target.checked)} 
                            />
                            <span>
                                Permanently delete all rule tables associated with this family.
                                <br />
                                <em style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                    (If left unchecked, these tables will remain in the database and fall back to "Global / Domain Rules")
                                </em>
                            </span>
                        </label>
                        <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                            <button type="button" className="btn-secondary" onClick={() => setModalType(null)} disabled={isSaving}>Cancel</button>
                            <button type="submit" className="btn-primary" style={{ background: 'var(--error)' }} disabled={isSaving}>
                                {isSaving ? 'Deleting...' : 'Confirm Delete'}
                            </button>
                        </div>
                    </form>
                </div>
            </Overlay>
        );
    }

    // ========================================================================
    // CONSISTENCY CHECK MODAL
    // ========================================================================
    if (modalType === 'consistencyCheck') {
        return (
            <Overlay>
                <div
                    className="modal-content"
                    style={{
                        width: 'min(720px, 92vw)',
                        maxWidth: '720px',
                        maxHeight: '85vh',
                        padding: '1.25rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1.25rem',
                        overflow: 'hidden'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.75rem', flexShrink: 0 }}>
                        <i className="fa-solid fa-list-check" style={{ fontSize: '1.5rem', color: 'var(--accent)' }}></i>
                        <div>
                            <h2 style={{ fontSize: '1.25rem', margin: 0 }}>Consistency Check</h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>Analysis for family: <strong>{modalTargetFamily?.name}</strong></p>
                        </div>
                    </div>

                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '0.25rem' }}>
                    {isCheckingConsistency ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem 0', gap: '1rem' }}>
                            <i className="fa-solid fa-arrows-spin fa-spin" style={{ fontSize: '2.5rem', color: 'var(--accent)' }}></i>
                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>Checking consistency...</span>
                        </div>
                    ) : consistencyResult?.error ? (
                        <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.08)', color: 'var(--error)', borderLeft: '3px solid var(--error)', borderRadius: '4px', fontSize: '0.9rem' }}>
                            <i className="fa-solid fa-circle-exclamation"></i> {consistencyResult.error}
                        </div>
                    ) : consistencyResult ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {consistencyResult.isConsistent ? (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem', background: 'rgba(16, 185, 129, 0.08)', color: 'var(--success)', borderLeft: '3px solid var(--success)', borderRadius: '4px' }}>
                                        <i className="fa-solid fa-circle-check" style={{ fontSize: '1.25rem', marginTop: '0.15rem' }}></i>
                                        <div>
                                            <strong style={{ display: 'block', marginBottom: '0.25rem' }}>Rules are Consistent</strong>
                                            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                                At least one valid combination of choices exists. We verified the configuration space and found <strong>{consistencyResult.validConfigurationsCount}</strong> valid combination(s).
                                            </span>
                                        </div>
                                    </div>

                                    {consistencyResult.unreachableValues?.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '1rem', background: 'rgba(245, 158, 11, 0.08)', color: '#D97706', borderLeft: '3px solid #D97706', borderRadius: '4px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600 }}>
                                                <i className="fa-solid fa-triangle-exclamation"></i> Unreachable Values Detected ({consistencyResult.unreachableValues.length})
                                            </div>
                                            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                These values exist in metadata but can never be selected under the current rulesets:
                                            </p>
                                            <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.85rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                {consistencyResult.unreachableValues.map((uv, idx) => (
                                                    <li key={idx} style={{ color: 'var(--text-primary)' }}>
                                                        Feature <strong>{uv.featureName}</strong> cannot be set to <strong>{uv.value}</strong>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {consistencyResult.sampleValidConfigurations?.length > 0 && (
                                        <div>
                                            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem' }}>Sample Valid Configurations</h3>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.75rem', background: 'var(--bg-secondary)', fontSize: '0.85rem' }}>
                                                {consistencyResult.sampleValidConfigurations.map((cfg, idx) => (
                                                    <div key={idx} style={{ paddingBottom: idx < consistencyResult.sampleValidConfigurations.length - 1 ? '0.5rem' : 0, borderBottom: idx < consistencyResult.sampleValidConfigurations.length - 1 ? '1px solid var(--border)' : 'none', color: 'var(--text-secondary)' }}>
                                                        <strong style={{ color: 'var(--text-primary)' }}>Config #{idx + 1}:</strong> {Object.entries(cfg).map(([k, v]) => `${k} = ${v}`).join(', ')}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem', background: 'rgba(239, 68, 68, 0.08)', color: 'var(--error)', borderLeft: '3px solid var(--error)', borderRadius: '4px' }}>
                                        <i className="fa-solid fa-circle-xmark" style={{ fontSize: '1.25rem', marginTop: '0.15rem' }}></i>
                                        <div>
                                            <strong style={{ display: 'block', marginBottom: '0.25rem' }}>Contradiction Found</strong>
                                            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                                There are no valid assignments of values that satisfy all active rulesets for this family.
                                            </span>
                                        </div>
                                    </div>

                                    {consistencyResult.conflicts?.length > 0 ? (
                                        <div style={{ marginTop: '0.5rem' }}>
                                            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem' }}>Contradiction Matrix</h3>
                                            <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '4px' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'left' }}>
                                                    <thead>
                                                        <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                                                            <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600 }}>If selection is...</th>
                                                            <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600 }}>Feature</th>
                                                            <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600 }}>Allowed values (Ruleset A)</th>
                                                            <th style={{ padding: '0.5rem 0.75rem', fontWeight: 600 }}>Allowed values (Ruleset B)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {consistencyResult.conflicts.map((c, i) => (
                                                            <tr key={i} style={{ borderBottom: i < consistencyResult.conflicts.length - 1 ? '1px solid var(--border)' : 'none' }}>
                                                                <td style={{ padding: '0.5rem 0.75rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                                                                    <code>{c.context}</code>
                                                                </td>
                                                                <td style={{ padding: '0.5rem 0.75rem' }}>
                                                                    <strong>{c.featureName}</strong>
                                                                </td>
                                                                <td style={{ padding: '0.5rem 0.75rem' }}>
                                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '2px' }}>{c.rulesetA}</div>
                                                                    <span style={{ background: 'rgba(220, 38, 38, 0.05)', color: 'var(--error)', padding: '2px 6px', borderRadius: '3px', border: '1px solid rgba(239, 68, 68, 0.15)', fontWeight: 500 }}>
                                                                        {c.rulesetAAllowed.join(', ') || 'none'}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.5rem 0.75rem' }}>
                                                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '2px' }}>{c.rulesetB}</div>
                                                                    <span style={{ background: 'rgba(37, 99, 235, 0.05)', color: 'var(--accent)', padding: '2px 6px', borderRadius: '3px', border: '1px solid rgba(37, 99, 235, 0.15)', fontWeight: 500 }}>
                                                                        {c.rulesetBAllowed.join(', ') || 'none'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem' }}>Conflict Details</h3>
                                            <ul style={{ margin: 0, paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                                {consistencyResult.inconsistentRuleSets?.map((conflict, i) => (
                                                    <li key={i}>{conflict}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : null}
                    </div>

                    <div className="modal-actions" style={{ marginTop: '0.5rem', flexShrink: 0 }}>
                        <button className="btn-secondary" onClick={() => setModalType(null)}>Close</button>
                    </div>
                </div>
            </Overlay>
        );
    }

    // ========================================================================
    // NO MODAL OPEN
    // ========================================================================
    return null;
}
